import React, { Suspense, useEffect, useState } from "react";
import { useDarkMode } from "../context/DarkModeContext";

// The "always running" layer: FKAmbientComp (soft aurora orbs + faint flight
// arcs) played full-bleed behind page content. Rules of engagement:
// - pointer-events: none, aria-hidden — it can never interfere.
// - CSS "cover" emulation (the 16:9 composition is centered and scaled to
//   cover the container, overflow clipped) so there is never letterboxing.
// - prefers-reduced-motion renders nothing (the page's static gradient bg
//   remains); the remotion chunk is lazy with a null fallback, so this adds
//   nothing to first paint.
// - The seek clock runs at 15fps — plenty for slow-drifting blur, half the
//   render work.
const PlayerCanvas = React.lazy(() => import("./PlayerCanvas"));

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches
  );
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return undefined;
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

/**
 * @param {object} props
 * @param {number} [props.intensity] 0..1-ish opacity multiplier passed to the
 *   composition (1 = marketing canvas, ~0.5 for busier app surfaces).
 * @param {boolean} [props.minimal] orbs only — for running OVER app content
 *   as a tint (no arc/dot lines across tables).
 * @param {string} [props.className] extra classes for the layer (e.g. "fixed"
 *   instead of the default "absolute", plus a z-index when overlaying).
 */
function AmbientBackdrop({ intensity = 1, minimal = false, className = "absolute" }) {
  const { isDarkMode } = useDarkMode();
  const reduced = usePrefersReducedMotion();
  if (reduced) return null;
  return (
    <div
      aria-hidden
      className={`pointer-events-none inset-0 overflow-hidden ${className}`}
    >
      {/* Cover: at least as wide as the box AND at least 16:9 of its height. */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        style={{ width: "max(100%, 178vh)", aspectRatio: "16 / 9" }}
      >
        <Suspense fallback={null}>
          <PlayerCanvas
            variant="ambient"
            width="100%"
            height="100%"
            dark={isDarkMode}
            tickFps={15}
            extraProps={{ intensity, minimal }}
          />
        </Suspense>
      </div>
    </div>
  );
}

export default AmbientBackdrop;
